import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, CircleDollarSign, CalendarDays } from "lucide-react";
import { postBooking } from "../../lib/api";
import { toast } from "sonner";

const TIMES = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];

const field = "w-full bg-[#0a0a0a] border border-white/10 focus:border-[#0055ff] outline-none px-4 py-3.5 text-sm transition-colors placeholder:text-white/30";
const label = "font-mono text-xs uppercase tracking-[0.15em] text-white/50 mb-2 block";

export const Booking = ({ services, preselectId, onConsumePreselect }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", car: "", date: "", time: "", notes: "" });
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preselectId) {
      setSelected((prev) => (prev.includes(preselectId) ? prev : [...prev, preselectId]));
      onConsumePreselect?.();
    }
  }, [preselectId, onConsumePreselect]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Name and phone are required");
    if (!form.car.trim()) return toast.error("Please add your vehicle details");
    if (!selected.length) return toast.error("Select at least one service");
    if (!form.date || !form.time) return toast.error("Choose a preferred date and time");
    setBusy(true);
    try {
      await postBooking({ ...form, services: selected });
      setDone(true);
      toast.success("Booking request sent! We'll confirm shortly.");
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="booking" className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-32" data-testid="booking">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#3377ff]">Reserve a slot</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-4 leading-[0.9]">
            Book your<br />detail
          </h2>
          <p className="text-white/50 mt-6 leading-relaxed max-w-sm">
            Pick your services and a preferred time. We&apos;ll confirm by phone and bring the studio to your driveway.
          </p>
          <div className="mt-8 flex items-start gap-3 border border-[#0055ff]/40 bg-[#0055ff]/10 p-4" data-testid="pay-in-person">
            <CircleDollarSign size={20} className="text-[#3377ff] shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">Pay in person</p>
              <p className="text-white/50 text-sm">No deposit online — settle up on the day once you&apos;re thrilled with the result.</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              className="h-full flex flex-col items-center justify-center text-center bg-[#0a0a0a] border border-white/10 p-12"
              data-testid="booking-success"
            >
              <div className="h-16 w-16 rounded-full bg-[#0055ff]/15 border border-[#0055ff]/40 flex items-center justify-center mb-6">
                <Check size={30} className="text-[#3377ff]" />
              </div>
              <h3 className="font-display text-3xl tracking-tight">Booking received</h3>
              <p className="text-white/50 mt-3 max-w-sm">
                Thanks {form.name.split(" ")[0]}! We&apos;ve sent your request through and will call you on {form.phone} to confirm.
              </p>
              <button
                onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", car: "", date: "", time: "", notes: "" }); setSelected([]); }}
                data-testid="booking-reset"
                className="cta-sharp mt-8 border border-white/25 hover:border-white px-7 py-3 font-mono text-xs uppercase tracking-[0.15em]"
              >
                New booking
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8" data-testid="booking-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={label}>Full name *</label>
                  <input className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="booking-name" />
                </div>
                <div>
                  <label className={label}>Phone *</label>
                  <input className={field} value={form.phone} onChange={set("phone")} placeholder="0400 000 000" data-testid="booking-phone" />
                </div>
                <div>
                  <label className={label}>Email (optional)</label>
                  <input className={field} type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="booking-email" />
                </div>
                <div>
                  <label className={label}>Vehicle *</label>
                  <input className={field} value={form.car} onChange={set("car")} placeholder="2021 Toyota Hilux" data-testid="booking-car" />
                </div>
              </div>

              <div className="mt-6">
                <label className={label}>Select services *</label>
                <div className="flex flex-wrap gap-2.5">
                  {services.map((s) => {
                    const on = selected.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggle(s.id)}
                        data-testid={`booking-service-${s.id}`}
                        className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-xs uppercase tracking-[0.1em] transition-colors ${
                          on ? "border-[#0055ff] bg-[#0055ff]/15 text-white" : "border-white/15 text-white/60 hover:border-white/40"
                        }`}
                      >
                        {on && <Check size={13} className="text-[#3377ff]" />}
                        {s.name} <span className="text-white/40">{s.price}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                <div>
                  <label className={label}>Preferred date *</label>
                  <div className="relative">
                    <input className={`${field} pr-10`} type="date" value={form.date} onChange={set("date")} min={new Date().toISOString().split("T")[0]} data-testid="booking-date" style={{ colorScheme: "dark" }} />
                    <CalendarDays size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className={label}>Preferred time *</label>
                  <select className={field} value={form.time} onChange={set("time")} data-testid="booking-time" style={{ colorScheme: "dark" }}>
                    <option value="">Select a time</option>
                    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className={label}>Notes (optional)</label>
                <textarea className={`${field} resize-none`} rows={3} value={form.notes} onChange={set("notes")} placeholder="Location, gate codes, condition of vehicle…" data-testid="booking-notes" />
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="booking-submit"
                className="cta-sharp mt-8 w-full bg-[#0055ff] hover:bg-[#3377ff] disabled:opacity-50 text-white py-4 font-mono text-xs uppercase tracking-[0.15em]"
              >
                {busy ? "Sending…" : "Request Booking"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
